import { Stack, Button, TextField, FormControl } from "@material-ui/core";
import { Restore } from "@material-ui/icons";
import { useEffect, useRef, useState } from "react";
import { FilePreview } from "../../../../../@types/listings/fileData";
import { useTranslation } from "react-i18next";
import { preventBubbling } from "utils/listings/helper";
import { BulkMessageFilePreviewCard } from "./BulkMessageDialogFileUpload";
import { processMessageTemplate } from "utils/contacts/bulkMessage/helper";
import type { CustomContactTemplateValues } from "utils/contacts/bulkMessage/helper";
import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import type {
  MessageStatus,
  SelectedMessageStatus,
  PlaceholderTemplateForm
} from "utils/contacts/bulkMessage/new";
import type { ProcessedListingData } from "../../../../../@types/sharedSchema";
import type { BulkMessageRecipient } from "utils/contacts/bulkMessage/new";

type BulkMessageDialogCustomMessageProps = {
  listing: ProcessedListingData | null;
  message: string;
  contact: BulkMessageRecipient;
  filesPreview: FilePreview[];
  messageStatus: MessageStatus;
  contactSkipped: boolean;
  sendingMessage: boolean;
  minisiteLink?: string;
  placeholderTemplatesForm: PlaceholderTemplateForm;
  perContactData?: Record<string, string>;
  setSelectedMessageStatus: Dispatch<SetStateAction<SelectedMessageStatus>>;
};

export function BulkMessageDialogCustomMessage({
  contact,
  message,
  listing,
  filesPreview,
  messageStatus,
  sendingMessage,
  contactSkipped,
  minisiteLink,
  placeholderTemplatesForm,
  perContactData,
  setSelectedMessageStatus
}: BulkMessageDialogCustomMessageProps): JSX.Element {
  const [customMessageInput, setCustomMessageInput] = useState("");
  const [editMode, setEditMode] = useState(false);

  const { t: translate } = useTranslation();

  const formRef = useRef<HTMLFormElement>(null);

  const { customMessage } = messageStatus;

  const parsedCustomMessage = (customMessage || message) ?? "";

  const trimmedCustomMessage = customMessageInput.trim();

  const isCustomMessageSameAsOriginal = message === trimmedCustomMessage;

  useEffect(() => {
    setCustomMessageInput(parsedCustomMessage);
  }, [parsedCustomMessage]);

  const handleChangeCustomMessage = ({
    target: { value }
  }: ChangeEvent<HTMLInputElement>): void => {
    setCustomMessageInput(value);
  };

  const handleCustomMessageSave = async (): Promise<void> => {
    setEditMode(false);

    const isTheSameAsOverride = trimmedCustomMessage === customMessage;
    const skipUpdatingCustomMessage = isCustomMessageSameAsOriginal || isTheSameAsOverride;

    if (skipUpdatingCustomMessage) {
      setCustomMessageInput(trimmedCustomMessage);

      setSelectedMessageStatus((prev) => ({
        ...prev,
        [contact.id]: {
          ...prev[contact.id],
          customMessage: trimmedCustomMessage
        }
      }));

      return;
    }

    setSelectedMessageStatus((prev) => ({
      ...prev,
      [contact.id]: {
        ...prev[contact.id],
        status: "pending",
        customMessage: trimmedCustomMessage
      }
    }));
  };

  const handleRestoreCustomMessage = async (): Promise<void> => {
    const isCustomMessageOverrideEmpty = !message;
    const skipResettingDescriptionOverride =
      isCustomMessageSameAsOriginal || isCustomMessageOverrideEmpty;

    if (skipResettingDescriptionOverride) {
      setCustomMessageInput(parsedCustomMessage);

      setSelectedMessageStatus((prev) => ({
        ...prev,
        [contact.id]: {
          ...prev[contact.id],
          customMessage: parsedCustomMessage
        }
      }));

      setEditMode(false);
      return;
    }

    setSelectedMessageStatus((prev) => ({
      ...prev,
      [contact.id]: {
        ...prev[contact.id],
        status: "pending",
        customMessage: ""
      }
    }));

    setEditMode(false);
    setCustomMessageInput(message);
  };

  const handleEnterEditMode = (): void => {
    if (sendingMessage) return;
    if (contactSkipped) return;

    setEditMode(true);
  };

  const customContactValues: CustomContactTemplateValues = {};

  if (minisiteLink !== undefined) {
    customContactValues.minisiteLink = minisiteLink;
  }

  const processedMessage = processMessageTemplate(
    parsedCustomMessage,
    contact,
    listing,
    placeholderTemplatesForm,
    undefined,
    customContactValues,
    perContactData
  );

  const showRestoreButton = !sendingMessage && !isCustomMessageSameAsOriginal;

  let editInputDisabled = sendingMessage;

  if (contactSkipped) editInputDisabled = true;

  return (
    <FormControl fullWidth ref={formRef} component="form">
      <Stack sx={{ mt: 2, my: 1 }} gap={1}>
        <Stack gap={1} onClick={preventBubbling()}>
          {editMode ? (
            <TextField
              required
              fullWidth
              multiline
              value={customMessageInput}
              maxRows={4}
              disabled={sendingMessage}
              placeholder={translate("contactsPage.bulkMessage.customMessage")}
              onBlur={handleCustomMessageSave}
              onChange={handleChangeCustomMessage}
            />
          ) : (
            <TextField
              fullWidth
              multiline
              value={processedMessage}
              maxRows={4}
              disabled={editInputDisabled}
              placeholder={translate("contactsPage.bulkMessage.customMessage")}
              onClick={handleEnterEditMode}
            />
          )}
          <Stack direction="row" gap={1} justifyContent="flex-start" alignItems="center">
            {showRestoreButton && (
              <Button
                sx={{ alignSelf: "start" }}
                size="small"
                color="info"
                variant="contained"
                startIcon={<Restore />}
                onClick={preventBubbling(handleRestoreCustomMessage)}
              >
                {translate("contactsPage.control.restoreOriginalMessage")}
              </Button>
            )}
          </Stack>
        </Stack>
        {filesPreview.map((file) => (
          <BulkMessageFilePreviewCard file={file} key={file.id} />
        ))}
      </Stack>
    </FormControl>
  );
}
